import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { useEffect, useState, Suspense, lazy } from 'react'
import { sessionStore } from './lib/sessionStore'
import { fmtTime } from './lib/time'
import NoSleep from 'nosleep.js'
import ErrorBoundary from './components/ErrorBoundary'
import SpotifyRemoteBar from './components/SpotifyRemoteBar'
import './styles/tokens.css'
import './styles/base.css'
import './index.css'
import { Spinner } from './components/ui'

if (process.env.NODE_ENV === 'production') {
  const startSentry = () => import('./lib/sentry').then(m => m.initSentry())
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(startSentry)
  } else {
    setTimeout(startSentry, 1500)
  }
}

// Lazy loading delle pagine per migliorare le performance
const AuthPage = lazy(() => import('./pages/AuthPage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Palestra = lazy(() => import('./pages/Palestra'))
const Hyrox = lazy(() => import('./pages/Hyrox'))
const Corsa = lazy(() => import('./pages/Corsa'))
const Peso = lazy(() => import('./pages/Peso'))
const Admin = lazy(() => import('./pages/Admin'))
const Profilo    = lazy(() => import('./pages/Profilo'))
const Lega       = lazy(() => import('./pages/Lega'))
const Dieta        = lazy(() => import('./pages/Dieta'))
const Piano        = lazy(() => import('./pages/Piano'))
const PianoSession = lazy(() => import('./pages/PianoSession'))
const Assessment   = lazy(() => import('./pages/Assessment'))
const Objectives   = lazy(() => import('./pages/Objectives'))
const Coach        = lazy(() => import('./pages/Coach'))
const EliteCompare = lazy(() => import('./pages/EliteCompare'))
const Supporto = lazy(() => import('./pages/Supporto'))
const Allena       = lazy(() => import('./pages/Allena'))
const Progressi    = lazy(() => import('./pages/Progressi'))
const AthleteSetup = lazy(() => import('./components/AthleteSetup'))

// Banco di prova visivo (/__design). Il ternario su NODE_ENV viene risolto a
// build time, così in produzione l'import sparisce insieme al chunk.
const DesignHarness = process.env.NODE_ENV === 'development'
  ? lazy(() => import('./pages/DesignHarness'))
  : null

// Componente di fallback per il loading
const PageLoader = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)'
  }}>
    <Spinner size={32} />
  </div>
)

// ── Wake Lock: schermo sempre acceso (iOS + Android) ───────
const noSleep = new NoSleep()

function WakeLock() {
  useEffect(() => {
    function enable() { noSleep.enable() }
    // Abilita al primo tap/touch (iOS richiede un gesto utente)
    document.addEventListener('touchstart', enable, { once: true })
    document.addEventListener('click',      enable, { once: true })
    return () => {
      noSleep.disable()
      document.removeEventListener('touchstart', enable)
      document.removeEventListener('click',      enable)
    }
  }, [])
  return null
}

// ── Floating banner quando sessione palestra è attiva ──────
export { fmtTime }

function SessionBanner() {
  const navigate  = useNavigate()
  const location  = useLocation()
  // session: ricaricato solo su eventi reali (non ogni secondo)
  const [session, setSession] = useState(() => sessionStore.load('PALESTRA'))
  // tick: incrementato ogni secondo — forza re-render per aggiornare elapsed
  const [tick, setTick] = useState(0)

  useEffect(() => {
    // Ricarica sessione quando si torna sulla pagina o si cambia tab
    const reload = () => setSession(sessionStore.load('PALESTRA'))
    const onVisibility = () => { if (document.visibilityState === 'visible') reload() }
    window.addEventListener('focus', reload)
    window.addEventListener('storage', reload)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('focus', reload)
      window.removeEventListener('storage', reload)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  // Tick solo quando c'è una sessione attiva — evita re-render globali inutili ogni 500ms
  useEffect(() => {
    if (!session) return
    const id = setInterval(() => setTick(t => t + 1), 500)
    return () => clearInterval(id)
  }, [session])

  // Ricarica anche quando si cambia pagina (es. tornando da /palestra)
  useEffect(() => {
    setSession(sessionStore.load('PALESTRA'))
  }, [location.pathname])

  if (!session || location.pathname === '/forza') return null

  const doneSets = Object.values(session.logs || {}).flat().filter(s => s.done).length
  // Elapsed calcolato sempre da startTime (timestamp fisso) — mai da session.elapsed stale
  const startTime = session.startTime
  const elapsed   = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0

  const restLeft  = session.restEndAt ? Math.max(0, Math.round((session.restEndAt - Date.now()) / 1000)) : 0
  const restPct   = session.restTotal && restLeft > 0 ? (restLeft / session.restTotal) * 100 : 0
  const restColor = restLeft < 10 ? '#EF4444' : restLeft < 30 ? '#C47E08' : '#FF3962'

  return (
    <div className="session-banner banner-in">
      <div
        className="session-banner-inner"
        onClick={() => navigate('/forza', { state: { resumePlanId: session.planId, autoResume: true } })}
        style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0 }}
      >
        {/* Riga principale */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Pulse dot */}
            <div style={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              background: '#EC0031', boxShadow: '0 0 8px rgba(255,57,98,0.9)',
              animation: 'pulse 1.5s ease infinite',
            }} />
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: 'rgba(36,35,37,0.63)', letterSpacing: '0.12em', textTransform: 'uppercase', lineHeight: 1, marginBottom: 3 }}>
                In corso · {doneSets} set
              </div>
              {/* Timer in giallo acido */}
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 900, color: '#FF3962', letterSpacing: '-1px', lineHeight: 1 }}>
                {fmtTime(elapsed)}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 900, color: '#fff', background: '#EC0031', padding: '7px 16px', borderRadius: 100 }}>
            Torna →
          </div>
        </div>

        {/* Barra recupero — solo se attivo */}
        {restLeft > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: restColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Recupero
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 900, color: restColor, lineHeight: 1 }}>
                {fmtTime(restLeft)}
              </div>
            </div>
            <div style={{ height: 2, background: 'rgba(36,35,37,0.12)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, background: restColor, width: `${restPct}%`, transition: 'width 0.5s linear' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ProtectedRoute({ children }) {
  const { user, profile, loading, fetchProfile } = useAuth()

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size={32} />
    </div>
  )

  if (!user) return <Navigate to="/auth" replace />

  if (profile && !profile.approved) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, textTransform: 'uppercase', marginBottom: 8 }}>In attesa di approvazione</h2>
      <p style={{ color: 'var(--text3)', fontSize: 14, maxWidth: 280 }}>Il tuo account è stato creato. Un admin deve approvare il tuo accesso. Riprova tra poco.</p>
    </div>
  )

  // First-time onboarding: athlete_context not yet set
  if (profile && profile.approved && profile.athlete_context == null) {
    return (
      <div style={{ minHeight: '100vh', background: '#FAF6EF' }}>
        <Suspense fallback={<PageLoader />}>
          <AthleteSetup onDone={() => fetchProfile(profile.id)} />
        </Suspense>
      </div>
    )
  }

  return children
}

function AppRoutes() {
  const { user } = useAuth()
  return (
    <>
      <SessionBanner />
      <SpotifyRemoteBar />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/auth" element={user ? <Navigate to="/" replace /> : (
            <Suspense fallback={<PageLoader />}>
              <AuthPage />
            </Suspense>
          )} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/forza"       element={<ProtectedRoute><Palestra /></ProtectedRoute>} />
          <Route path="/resistenza"  element={<ProtectedRoute><Corsa /></ProtectedRoute>} />
          <Route path="/stato"       element={<ProtectedRoute><Peso /></ProtectedRoute>} />
          <Route path="/hyrox"       element={<ProtectedRoute><Hyrox /></ProtectedRoute>} />
          {/* redirect backward-compat */}
          <Route path="/palestra"    element={<Navigate to="/forza" replace />} />
          <Route path="/corsa"       element={<Navigate to="/resistenza" replace />} />
          <Route path="/peso"        element={<Navigate to="/stato" replace />} />
          <Route path="/admin"    element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/profilo"   element={<ProtectedRoute><Profilo /></ProtectedRoute>} />
          <Route path="/profilo/lega" element={<ProtectedRoute><Lega /></ProtectedRoute>} />
          <Route path="/ai-piano" element={<Navigate to="/piano" replace />} />
          <Route path="/piano"          element={<ProtectedRoute><Piano /></ProtectedRoute>} />
          <Route path="/piano/sessione" element={<ProtectedRoute><PianoSession /></ProtectedRoute>} />
          <Route path="/assessment"     element={<ProtectedRoute><Assessment /></ProtectedRoute>} />
          <Route path="/objectives"     element={<ProtectedRoute><Objectives /></ProtectedRoute>} />
          <Route path="/coach"          element={<ProtectedRoute><Coach /></ProtectedRoute>} />
          <Route path="/supporto" element={<Supporto />} />
          <Route path="/confronto"      element={<ProtectedRoute><EliteCompare /></ProtectedRoute>} />
          {/* Destinazioni di primo livello della bottom nav. */}
          <Route path="/allena"         element={<ProtectedRoute><Allena /></ProtectedRoute>} />
          <Route path="/progressi"      element={<ProtectedRoute><Progressi /></ProtectedRoute>} />
          <Route path="/dieta"          element={<ProtectedRoute><Dieta /></ProtectedRoute>} />
          {/* Banco di prova visivo: solo in sviluppo, mai nel bundle di produzione. */}
          {DesignHarness && <Route path="/__design" element={<DesignHarness />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <WakeLock />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
